import { useState, useEffect } from 'react';
import { Dialog, DialogPanel, Title, Text, TextInput, Button } from '@tremor/react';
import useSWR from 'swr';
import { authApi, googleSignInUrl, microsoftSignInUrl } from '../lib/auth';

interface LoginModalProps {
  isOpen: boolean;
  onSuccess: () => void;
  errorMessage?: string | null;
}

export function LoginModal({ isOpen, onSuccess, errorMessage }: LoginModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: providers } = useSWR(isOpen ? '/api/auth/providers' : null, () => authApi.providers());

  useEffect(() => { if (isOpen) setError(null); }, [isOpen]);

  const handleSubmit = async () => {
    if (!email.trim() || !password) {
      setError('Email and password are required');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await authApi.login(email.trim(), password);
      onSuccess();
    } catch (err) {
      const e = err as Error;
      setError(e.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={() => {}} static={true}>
      <DialogPanel className="bg-gray-900 border border-gray-800 max-w-md">
        <Title className="text-gray-100 text-center">Sign in to HarmCheck Console</Title>
        <Text className="mt-2 text-gray-400 text-center">
          Staff access only. Sign in with your Alphy account.
        </Text>

        <div className="mt-4 space-y-4">
          <div>
            <label htmlFor="login-email" className="block text-sm text-gray-300 mb-1.5">Email</label>
            <TextInput
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="you@alphyco.com"
              className="bg-gray-800 border-gray-700"
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="login-password" className="block text-sm text-gray-300 mb-1.5">Password</label>
            <TextInput
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="Password"
              className="bg-gray-800 border-gray-700"
            />
          </div>
        </div>

        {(error || errorMessage) && (
          <div className="mt-3 bg-red-950/30 border border-red-900/50 rounded p-2">
            <Text className="text-red-400 text-sm">{error || errorMessage}</Text>
          </div>
        )}

        <Button onClick={handleSubmit} loading={isLoading} className="mt-6 w-full">
          Sign in
        </Button>

        {(providers?.google || providers?.microsoft) && (
          <>
            <div className="mt-6 mb-3 flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-800" />
              <Text className="text-xs text-gray-500">or</Text>
              <div className="flex-1 h-px bg-gray-800" />
            </div>
            <div className="space-y-2">
              {providers?.google && (
                <a
                  href={googleSignInUrl()}
                  className="block w-full text-center px-4 py-2 rounded bg-gray-800 hover:bg-gray-700 text-gray-100 text-sm font-medium border border-gray-700"
                >
                  Continue with Google
                </a>
              )}
              {providers?.microsoft && (
                <a
                  href={microsoftSignInUrl()}
                  className="block w-full text-center px-4 py-2 rounded bg-gray-800 hover:bg-gray-700 text-gray-100 text-sm font-medium border border-gray-700"
                >
                  Continue with Microsoft
                </a>
              )}
            </div>
          </>
        )}
      </DialogPanel>
    </Dialog>
  );
}
